import {gql} from '@apollo/client'
import React, {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {LinkCrumbtrail} from '../../components/crumbtrail'
import {
  PageBody,
  PageDescription,
  PageHeader,
  PageHeaderContent
} from '../../components/page-styles'
import styled from 'styled-components'
import {Loader} from '../../components/loader'
import {
  PermissionAction,
  PermissionResource
} from '../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../environment'
import {useAnalytics} from '../../lib/analytics/analytics'
import {CancellationForm} from './filter'
import {CardFilterFieldsBase} from '../../common/cards/synced-card-feed-filter-query'
import {useCancelCardsMutation} from './generated'
import {ResultContainer} from './results'
import {CancellationReviewModal} from './review'
import {NoResultsPermissions} from '../../components/library/list'
import {useActionFlowConfig} from '../../hooks/use-action-flow-config'

export const CancelCards = ({environment}: {environment: EnvironmentNode}) => {
  const analytics = useAnalytics()
  const canUse = environment.hasPermission(
    PermissionResource.CardInstance,
    PermissionAction.Admin
  )
  const {data: actionFlowConfigs, loading} = useActionFlowConfig({
    environmentId: environment.id
  })

  const [cancelCards, {loading: performingCancel}] = useCancelCardsMutation({
    refetchQueries: ['CardCancelTasks']
  })

  const [reviewCriteria, setReviewCriteria] = useState<
    CardFilterFieldsBase | undefined
  >(undefined)

  const [formKey, setFormKey] = useState<number>(Math.random())

  useEffect(() => {
    analytics.track({
      event: 'Viewed cancel cards tool',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'tools'
      }
    })
  }, [analytics, environment])

  if (loading || !actionFlowConfigs) {
    return <Loader />
  }

  return (
    <>
      <Helmet>
        <title>Cancel cards</title>
      </Helmet>
      <PageHeader>
        <LinkCrumbtrail crumbs={[['../tools', 'Tools'], 'Operations']} />
        <PageHeaderContent>
          <h2>Cancel cards</h2>
        </PageHeaderContent>
      </PageHeader>
      <PageDescription>
        Cancel cards matching one or more of the following criteria. Cards can
        also be cancelled via API.
      </PageDescription>
      <PageBody>
        {canUse ? (
          <Wrapper>
            <Filters data-testid="cancel-cards-form">
              <CancellationForm
                actionFlowConfigs={actionFlowConfigs}
                reviewCancellation={criteria => {
                  setReviewCriteria(criteria)
                }}
                key={formKey}
              />
            </Filters>
            <Log>
              <ResultContainer
                actionFlowConfigs={actionFlowConfigs}
                environment={environment}
              />
            </Log>
          </Wrapper>
        ) : (
          <NoResultsPermissions message="Sorry, you don't have permission to use this tool" />
        )}
      </PageBody>

      {reviewCriteria && (
        <CancellationReviewModal
          environment={environment}
          actionFlowConfigs={actionFlowConfigs}
          criteria={reviewCriteria}
          isPerformingCancel={performingCancel}
          onComplete={async criteria => {
            if (!performingCancel) {
              await cancelCards({
                variables: {
                  organisationId: environment.organisationId,
                  environmentId: environment.id,
                  criteria
                }
              })

              // reset form and criteria for review
              setFormKey(Math.random())
              setReviewCriteria(undefined)
            }
          }}
          onCancel={() => setReviewCriteria(undefined)}
        />
      )}
    </>
  )
}

const Filters = styled.div`
  flex: 1;
`

const Log = styled.div`
  flex: 1;
`

const Wrapper = styled.div<{disabled?: boolean}>`
  display: flex;
  gap: 30px;

  ${props =>
    props.disabled &&
    `
      opacity: 0.3;
      pointer-events: none;
    `}
`

gql`
  mutation CancelCards(
    $organisationId: String!
    $environmentId: String!
    $criteria: JSON!
  ) {
    createCardCancelTask(
      input: {
        cardCancelTask: {
          organisationId: $organisationId
          environmentId: $environmentId
          criteria: $criteria
        }
      }
    ) {
      cardCancelTask {
        id
      }
    }
  }
`
